(() => {
  const AVAILABILITY_PARAM = 'filter.v.availability';
  const PRODUCT_SELECTOR = '[data-stock-product]';
  let activeController;
  let requestVersion = 0;
  let scheduledFrame;

  function numberFrom(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function currentState() {
    const results = document.getElementById('filter-results');
    const meta = results?.querySelector('.js-stock-order-meta');
    const grid = results?.querySelector('[data-stock-order-grid]');

    if (!results || !meta || !grid) return null;

    return {
      results,
      grid,
      sectionId: meta.dataset.sectionId,
      currentPage: numberFrom(meta.dataset.currentPage, 1),
      totalPages: numberFrom(meta.dataset.totalPages, 1),
      pageSize: numberFrom(meta.dataset.pageSize, 24),
      totalItems: numberFrom(meta.dataset.totalItems)
    };
  }

  function parseSection(html) {
    const template = document.createElement('template');
    template.innerHTML = html;

    const results = template.content.querySelector('#filter-results');
    const meta = results?.querySelector('.js-stock-order-meta');
    if (!results || !meta) throw new Error('No se pudo leer la sección de productos.');

    const grid = results.querySelector('[data-stock-order-grid]');
    if (!grid) throw new Error('No se pudo leer la cuadrícula de productos.');

    const cards = Array.from(grid.querySelectorAll(`:scope > ${PRODUCT_SELECTOR}`), (card) => ({
      id: card.dataset.productId || '',
      available: card.dataset.productAvailable === 'true',
      html: card.outerHTML
    }));

    return {
      cards,
      totalItems: numberFrom(meta.dataset.totalItems),
      totalPages: numberFrom(meta.dataset.totalPages, 1),
      pageSize: numberFrom(meta.dataset.pageSize, cards.length || 1)
    };
  }

  function availabilityMode(params) {
    const values = [...new Set(params.getAll(AVAILABILITY_PARAM))];
    return values.length === 1 && (values[0] === '0' || values[0] === '1') ? 'single' : 'all';
  }

  function capturePromos(grid) {
    const placements = [];
    let productIndex = 0;

    Array.from(grid.children).forEach((item) => {
      if (item.matches(PRODUCT_SELECTOR)) {
        productIndex += 1;
      } else if (!item.classList.contains('stock-order-divider')) {
        placements.push({ index: productIndex, html: item.outerHTML });
      }
    });

    return placements;
  }

  function dividerMarkup() {
    return `
      <li class="stock-order-divider" role="separator" style="grid-column: 1 / -1; border-top: 1px solid rgba(0, 0, 0, 0.18); margin-top: 1rem; padding-top: 1.5rem;">
        <h2 class="h4" style="margin: 0;">Agotados</h2>
      </li>`;
  }

  function renderCards(grid, cards, promos, dividerIndex = -1) {
    const promosByIndex = new Map();
    promos.forEach((promo) => {
      const index = Math.min(Math.max(promo.index, 0), cards.length);
      const group = promosByIndex.get(index) || [];
      group.push(promo.html);
      promosByIndex.set(index, group);
    });

    const output = [];
    for (let index = 0; index <= cards.length; index += 1) {
      const promoHtml = promosByIndex.get(index);
      if (promoHtml) output.push(...promoHtml);
      if (index === dividerIndex) output.push(dividerMarkup());
      if (index < cards.length) output.push(cards[index].html);
    }

    grid.innerHTML = output.join('');
  }

  async function orderCurrentPage() {
    const version = ++requestVersion;
    if (activeController) activeController.abort();
    activeController = new AbortController();

    const existingResults = document.getElementById('filter-results');
    existingResults?.classList.remove('is-loading');
    existingResults?.removeAttribute('aria-busy');

    const params = new URLSearchParams(window.location.search);
    if (availabilityMode(params) === 'single') return;

    const state = currentState();
    if (!state || state.totalItems < 2 || !state.sectionId) return;

    const { signal } = activeController;
    const baseParams = new URLSearchParams(params);
    baseParams.delete('page');
    baseParams.delete('section_id');
    baseParams.delete(AVAILABILITY_PARAM);

    const pageCache = new Map();
    const promos = capturePromos(state.grid);
    state.results.classList.add('is-loading');
    state.results.setAttribute('aria-busy', 'true');
    delete state.results.dataset.stockOrderReady;

    async function fetchPage(availability, page) {
      const cacheKey = `${availability ?? 'all'}:${page}`;
      if (pageCache.has(cacheKey)) return pageCache.get(cacheKey);

      const fetchParams = new URLSearchParams(baseParams);
      if (availability !== null) fetchParams.append(AVAILABILITY_PARAM, availability);
      if (page > 1) fetchParams.set('page', String(page));
      fetchParams.set('section_id', state.sectionId);

      const promise = fetch(`${window.location.pathname}?${fetchParams.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal
      }).then(async (response) => {
        if (!response.ok) throw new Error(`No se pudo cargar la colección (${response.status}).`);
        return parseSection(await response.text());
      });

      pageCache.set(cacheKey, promise);
      return promise;
    }

    async function groupRange(availability, from, to, pageSize) {
      if (to <= from) return [];

      const firstPage = Math.floor(from / pageSize) + 1;
      const lastPage = Math.floor((to - 1) / pageSize) + 1;
      const requests = [];
      for (let page = firstPage; page <= lastPage; page += 1) {
        requests.push(fetchPage(availability, page));
      }

      const pages = await Promise.all(requests);
      const cards = pages.flatMap((page) => page.cards);
      const offset = from - ((firstPage - 1) * pageSize);
      return cards.slice(offset, offset + (to - from));
    }

    try {
      const [availablePage, soldOutPage] = await Promise.all([
        fetchPage('1', 1),
        fetchPage('0', 1)
      ]);

      if (version !== requestVersion) return;

      const groupsAreDisjoint =
        availablePage.totalItems + soldOutPage.totalItems === state.totalItems &&
        availablePage.cards.every((card) => card.available) &&
        soldOutPage.cards.every((card) => !card.available) &&
        availablePage.pageSize === state.pageSize &&
        soldOutPage.pageSize === state.pageSize;

      if (!groupsAreDisjoint) {
        throw new Error('Shopify no devolvió grupos de disponibilidad consistentes.');
      }

      const sliceStart = (state.currentPage - 1) * state.pageSize;
      const sliceEnd = Math.min(sliceStart + state.pageSize, state.totalItems);
      const availableTotal = availablePage.totalItems;
      const availableFrom = Math.min(sliceStart, availableTotal);
      const availableTo = Math.min(sliceEnd, availableTotal);
      const soldOutFrom = Math.max(0, sliceStart - availableTotal);
      const soldOutTo = Math.max(0, sliceEnd - availableTotal);

      const [availableCards, soldOutCards] = await Promise.all([
        groupRange('1', availableFrom, availableTo, availablePage.pageSize),
        groupRange('0', soldOutFrom, soldOutTo, soldOutPage.pageSize)
      ]);
      const pageCards = [...availableCards, ...soldOutCards];

      const uniqueKeys = new Set(pageCards.map((card) => card.id || card.html));
      const cardsMatchGroups =
        pageCards.length === sliceEnd - sliceStart &&
        uniqueKeys.size === pageCards.length &&
        availableCards.every((card) => card.available) &&
        soldOutCards.every((card) => !card.available);

      if (!cardsMatchGroups) {
        throw new Error('Shopify cambió los resultados mientras se ordenaba la página.');
      }

      if (version !== requestVersion) return;

      const firstSoldOutIndex = Math.max(0, availableTotal - sliceStart);
      const dividerIndex = soldOutCards.length > 0 && firstSoldOutIndex < pageCards.length
        ? firstSoldOutIndex
        : -1;

      renderCards(state.grid, pageCards, promos, dividerIndex);
      state.results.dataset.stockOrderReady = 'true';
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('No se pudo ordenar por disponibilidad.', error);
    } finally {
      if (version === requestVersion) {
        state.results.classList.remove('is-loading');
        state.results.removeAttribute('aria-busy');
      }
    }
  }

  function scheduleOrder() {
    if (scheduledFrame) window.cancelAnimationFrame(scheduledFrame);
    scheduledFrame = window.requestAnimationFrame(() => {
      scheduledFrame = undefined;
      orderCurrentPage();
    });
  }

  document.addEventListener('on:facet-filters:updated', scheduleOrder);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleOrder();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleOrder, { once: true });
  } else {
    scheduleOrder();
  }
})();
